import React, { Fragment } from 'react';
import { Button, Form, Header, Segment, Dropdown, Checkbox, Grid, List } from 'semantic-ui-react';
import { inject, observer } from 'mobx-react';
import { observable, computed, toJS, extendObservable } from 'mobx';
import { withRouter } from 'react-router-dom';
import UiShared from 'stores/UiShared';
import Auth from 'stores/Auth';
import Account from 'stores/Account';
import MsgStore from 'stores/MsgStore';
import MessageBox from 'components/MessageBox';
import styles from './styles';
import Practice from 'stores/Practice';
import PracticeProgram from 'stores/PracticeProgram';
import states from 'data/states';
import practiceTypes from 'data/practiceTypes';
import API from 'api';
import _ from 'lodash';
import * as yup from 'yup';

@withRouter
@inject(root => ({
	auth: root.auth,
	user: root.auth.user,
	uiShared: root.uiShared
}))
@observer
export default class Edit extends React.Component {
	props: {
		auth?: Auth,
		user?: Account,
		uiShared?: UiShared
	};

	@computed
	get isNew() {
		return !this.opractice.id;
	}

	@observable opractice: Practice = new Practice();
	@observable loading: boolean = false;
	@observable showInviteDlg: boolean = false;
	@observable prescribers = [];
	@observable delegates = [];
	@observable therapeuticAreas: Array<object> = [];
	@observable programs: Array<object> = [];

	msgStore = new MsgStore();

	componentDidMount = async () => {
		try {
			const { match, uiShared } = this.props;
			this.loading = true;
			await this.fetchLists();
			if (match.params.id === '0') {
				this.opractice = new Practice();
				this.loading = false;
			} else {
				this.opractice = await Practice.loadById(parseInt(match.params.id));
				this.programs = this.programs.map(el => extendObservable(el, { checked: false }));
				this.setPrograms();
			}
		} finally {
			this.loading = false;
		}
	};

	fetchLists = async () => {
		await Promise.all([this.loadTherapeuticAreas(), this.loadPrograms()]);
	};

	loadTherapeuticAreas = async () => {
		const res = await API.get('/TherapeuticAreas');
		this.therapeuticAreas = res.map(el => {
			return { key: el.id, value: el.id, text: el.name };
		});
	};

	loadPrograms = async () => {
		const res = await API.get('/Programs');
		this.programs = res.map(el => {
			return { key: el.id, value: el.id, text: el.name };
		});
	};

	setPrograms = () => {
		const { opractice } = this;
		this.programs.forEach(p => {
			p.checked = opractice.PracticePrograms.find(pp => pp.programID === p.value) ? true : false;
		});
	};

	validate = async () => {
		let practiceCodeTaken = false;
		const { opractice } = this;
		if (opractice.practiceCode && opractice.therapeuticAreaID) {
			const res = await API.get(
				`/Practices?filter=${JSON.stringify({
					where: {
						and: [
							{ practiceCode: opractice.practiceCode },
							{ therapeuticAreaID: opractice.therapeuticAreaID }
						]
					}
				})}`
			);
			if (res && res.length > 0) {
				const isUpdating = !!(
					opractice.id &&
					res.length === 1 &&
					opractice.id === res[0].id &&
					opractice.practiceCode === res[0].practiceCode
				);
				practiceCodeTaken = !isUpdating;
			}
		}
		let validationData = Object.assign({}, opractice, {
			practiceCodeUnique: !practiceCodeTaken
		});
		await this.ValidationSchemaPractice.validate(validationData, {
			abortEarly: false
		});
	};

	ValidationSchemaPractice = yup.object().shape({
		practiceCode: yup
			.string()
			.label('Practice Code')
			.required()
			.matches(/^[0-9]*$/, {
				message: 'Only numeric characters are allowed for Practice Code',
				excludeEmptyString: true
			}),
		practiceCodeUnique: yup
			.boolean()
			.test(
				'len',
				'Practice Code must be unique within selected Therapeutic area',
				val => val === true
			),
		practiceName: yup
			.string()
			.label('Practice Name')
			.required(),
		practiceAddress: yup
			.string()
			.label('Address')
			.required(),
		practiceSuburb: yup
			.string()
			.label('Suburb')
			.required(),
		practicePostcode: yup
			.string()
			.label('Postcode')
			.required()
			.matches(/^[0-9]*$/, {
				message: 'Only numeric characters are allowed for Postcode',
				excludeEmptyString: true
			})
			.test('len', 'Postcode must be 4 digits.', val => (val ? val.length === 4 : true)),
		practiceState: yup
			.string()
			.label('State')
			.required(),
		practicePhone: yup
			.string()
			.nullable()
			.matches(/^[0-9]*$/, {
				message: 'Only numeric characters are allowed for Phone Number',
				excludeEmptyString: true
			}),
		practiceFAX: yup
			.string()
			.nullable()
			.matches(/^[0-9]*$/, {
				message: 'Only numeric characters are allowed for Fax Number',
				excludeEmptyString: true
			}),
		therapeuticAreaID: yup
			.number()
			.label('Therapeutic Area')
			.nullable()
			.required(),
		practiceType: yup
			.string()
			.label('Practice Type')
			.required(),
		disabled: yup
			.boolean()
			.label('Disabled')
			.required(),
		practiceProviderNo: yup
			.string()
			.nullable()
			.label('Provider No')
			.when('practiceType', {
				is: 'Hospital',
				then: yup.string().required()
			})
	});

	save = async () => {
		this.msgStore.clear();
		const { opractice } = this;
		let uiShared = this.props.uiShared;
		try {
			uiShared.showModal('Saving...');
			this.programs.forEach(p => {
				const initChecked = opractice.PracticePrograms.find(pp => pp.programID === p.value);
				const remove = initChecked && !p.checked;
				const add = !initChecked && p.checked;
				if (remove) {
					const practiceProgram = opractice.PracticePrograms.find(pp => pp.programID === p.value);
					practiceProgram.isDeleted = true;
				} else if (add) {
					opractice.PracticePrograms.push(new PracticeProgram({ programID: p.value }));
				}
			});
			await this.validate();
			await opractice.update();
			await opractice.reload();
			this.setPrograms();
			this.msgStore.setInfo('The record was saved.');
		} catch (err) {
			console.log(err);
			this.msgStore.setError(err);
		} finally {
			uiShared.hideModal();
		}
	};

	render() {
		const { history } = this.props;
		const { opractice, therapeuticAreas, programs } = this;
		return (
			<Segment loading={this.loading}>
				<Header as="h1">{opractice.id ? 'Edit Practice Details' : 'Add New Practice'}</Header>
				<Grid>
					<Grid.Column style={{ maxWidth: 750 }}>
						<Form>
							<Form.Group className={styles.formGroup}>
								<Form.Input
									label="Practice Code"
									value={opractice.practiceCode || ''}
									onChange={e => (this.opractice.practiceCode = e.target.value.toUpperCase())}
									width={8}
									required
								/>
								<div className="eight wide field"> </div>
								<Form.Input
									label="Practice Name"
									value={opractice.practiceName || ''}
									onChange={e => (this.opractice.practiceName = e.target.value.toUpperCase())}
									width={16}
									required
								/>

								<Form.Input
									label="Address"
									value={opractice.practiceAddress || ''}
									onChange={e => (this.opractice.practiceAddress = e.target.value.toUpperCase())}
									width={16}
									required
								/>

								<Form.Input
									label="Suburb"
									value={opractice.practiceSuburb || ''}
									onChange={e => (this.opractice.practiceSuburb = e.target.value.toUpperCase())}
									width={8}
									required
								/>
								<Form.Input
									label="Postcode"
									value={opractice.practicePostcode || ''}
									onChange={e => (this.opractice.practicePostcode = e.target.value)}
									width={8}
									required
								/>

								<Form.Field width={8} required>
									<label>State</label>
									<Dropdown
										selection
										placeholder="Select State"
										value={opractice.practiceState || ''}
										onChange={(e, data) => {
											this.opractice.practiceState = data.value;
										}}
										options={states}
										fluid
									/>
								</Form.Field>

								<div className="eight wide field"> </div>

								<Form.Input
									label="Phone Number"
									value={opractice.practicePhone || ''}
									onChange={e => (this.opractice.practicePhone = e.target.value)}
									width={8}
								/>
								<Form.Input
									label="Fax Number"
									value={opractice.practiceFAX || ''}
									onChange={e => (this.opractice.practiceFAX = e.target.value)}
									width={8}
								/>

								<Form.Field width={8} required>
									<label>Therapeutic Area</label>
									<Dropdown
										options={therapeuticAreas}
										value={opractice.therapeuticAreaID || ''}
										onChange={(e, data) => {
											this.opractice.therapeuticAreaID = data.value;
										}}
										width={8}
										selection
										placeholder="Select Therapeutic Area"
									/>
								</Form.Field>
								<Form.Field width={8} required>
									<label>Practice Type</label>
									<Dropdown
										selection
										placeholder="Select Practice Type"
										value={opractice.practiceType || ''}
										onChange={(e, data) => {
											this.msgStore.clear();
											this.opractice.practiceType = data.value;
											console.log(
												'onchange this.opractice.practiceType',
												this.opractice.practiceType
											);
										}}
										options={practiceTypes}
										fluid
									/>
								</Form.Field>

								{opractice.practiceType?.toLowerCase() === 'hospital' && (
									<Fragment>
										<div className="eight wide field"> </div>
										<Form.Input
											required
											label="Provider No"
											value={opractice.practiceProviderNo || ''}
											onChange={e => (this.opractice.practiceProviderNo = e.target.value)}
											width={8}
										/>
									</Fragment>
								)}

								<div className="sixteen wide field">
									<div className={styles.programsMargin}>
										<Header as="h4">Programs</Header>
										<List relaxed="very">
											{programs.map((el, idx) => (
												<List.Item key={idx}>
													<Checkbox
														checked={el.checked}
														onChange={() => {
															el.checked = !el.checked;
														}}
														label={` ${el.value} - ${el.text}` || ''}
													/>
												</List.Item>
											))}
										</List>
									</div>
								</div>

								<div className={styles.togglesCnt}>
									<Form.Field width={16} className={styles.toggleMargin}>
										<Checkbox
											label="Disable practice"
											checked={opractice.disabled || false}
											onChange={() => (this.opractice.disabled = !opractice.disabled)}
											toggle
										/>
									</Form.Field>
									<Form.Field width={16} className={styles.toggleMargin}>
										<Checkbox
											label="Scheduled Reminders"
											checked={opractice.scheduledReminders || false}
											onChange={() =>
												(this.opractice.scheduledReminders = !opractice.scheduledReminders)
											}
											toggle
										/>
									</Form.Field>
								</div>
							</Form.Group>
							<div>
								<Button
									disabled={this.isLoading}
									primary
									onClick={() => this.save()}
									floated="left"
								>
									Save
								</Button>
								<Button onClick={() => history && history.goBack()} floated="left">
									Back
								</Button>
								<div className={styles.clear} />
							</div>
						</Form>
					</Grid.Column>
				</Grid>
				<MessageBox msgStore={this.msgStore} />
			</Segment>
		);
	}
}



// WEBPACK FOOTER //
// ./src/routes/Admin/Practices/Edit.js